import { CommandClient } from "@formant/core/client/CommandClient";
import { defined } from "@formant/core/common/defined";
import { ICommandStore } from "@formant/core/model/data/ICommandStore";
import { duration } from "@formant/core/model/duration";
import { ICommand } from "@formant/core/model/ICommand";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class CommandStore implements ICommandStore {
    private cache = new StoreCache<Uuid, ICommand | false>({
        staleIntervalMs: 3 * duration.second,
    });
    private listCache = new StoreCache<Uuid, ICommand[]>({
        staleIntervalMs: 3 * duration.second,
    });

    constructor(
        private commandClient: CommandClient,
        private authStore: IAuthStore,
    ) {}

    get(id: Uuid): ICommand | false | undefined {
        return this.cache.get(
            id,
            async () =>
                (await this.commandClient.get(
                    await this.authStore.token(),
                    id,
                )) || false,
        );
    }

    async fetch(id: Uuid): Promise<ICommand | undefined> {
        const cached = this.get(id);
        if (cached) {
            return cached;
        }
        const cmd = await this.commandClient.get(
            await this.authStore.token(),
            id,
        );
        if (cmd) {
            this.cache.set(id, cmd);
        }
        return cmd;
    }

    list(deviceId: Uuid): ICommand[] | undefined {
        return this.listCache.get(deviceId, async () =>
            this.fetchList(deviceId),
        );
    }

    async fetchList(deviceId: Uuid) {
        const { items } = await this.commandClient.getUndelivered(
            await this.authStore.token(),
            { deviceId },
        );
        this.listCache.set(deviceId, items);
        return items;
    }

    async create(command: ICommand): Promise<ICommand> {
        const created = await this.commandClient.create(
            await this.authStore.token(),
            command,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    async update(id: Uuid, device: Partial<ICommand>) {
        const updated = await this.commandClient.update(
            await this.authStore.token(),
            id,
            device,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    async cancel(id: Uuid) {
        const result = await this.update(id, {
            canceledAt: new Date().toISOString(),
        });
        this.listCache.clear();
        return result;
    }

    isCommandComplete(deviceId: Uuid, commandId: Uuid): boolean | undefined {
        const commandList = this.list(deviceId);
        if (commandList === undefined) {
            return undefined;
        }
        for (const command of commandList) {
            if (command.id === commandId) {
                return command.success === true;
            }
        }
        return true;
    }

    getCurrentOnDemandRequests(deviceId: Uuid): ICommand[] {
        const commandList = this.list(deviceId);
        if (commandList === undefined) {
            return [];
        }
        const dataRequests = [];
        for (const command of commandList) {
            if (command.command === "formant.demand_data") {
                dataRequests.push(command);
            }
        }
        return dataRequests;
    }
}
